import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";

import useCart from "@/cart/useCart";
import { looksLikeReferralCode, resolveReferral } from "@/cart/referralCode";

/**
 * Whether the visitor's referral code gives a free bag pass with each Matic.
 *
 * Starts false and only turns true once the code's reward is confirmed, so a
 * card whose price depends on it renders its normal price first and never
 * flashes a referral treatment it then has to take back. The code on the cart
 * wins over the query parameter, since it is the one checkout will apply.
 */
const useReferralIncludesBagPass = (): boolean => {
  const searchParams = useSearchParams();
  const cartReferralCode = useCart((state) => state.referralCode);
  const candidate = cartReferralCode ?? searchParams.get("utm_term");
  const [includesBagPass, setIncludesBagPass] = useState(false);

  useEffect(() => {
    const trimmedCode = candidate?.trim() ?? "";

    if (!looksLikeReferralCode(trimmedCode)) {
      setIncludesBagPass(false);
      return;
    }

    let cancelled = false;

    resolveReferral(trimmedCode)
      .then((referral) => {
        if (!cancelled) {
          setIncludesBagPass(referral !== null && referral.reward !== "none");
        }
      })
      .catch((error) => {
        console.error("Failed to resolve referral reward:", error);
        if (!cancelled) {
          setIncludesBagPass(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [candidate]);

  return includesBagPass;
};

export default useReferralIncludesBagPass;
